/**
 * The letter as it will arrive, with handles on it.
 *
 * Each block is drawn by `renderBlock` - the same function that builds the mail
 * - and injected as markup. That is deliberate and it is the single most useful
 * property of this screen: there is no second renderer to drift, so a block that
 * looks right here looks right in the inbox. Every builder that keeps a
 * "preview mode" separate from an "edit mode" eventually ships the difference
 * between the two.
 *
 * `dangerouslySetInnerHTML` is safe here in the way that matters: the string
 * comes from `renderBlock`, which escapes every operator-supplied value and
 * strips scripts and event handlers from the two rich-text blocks (see
 * `render.ts`). Nothing reaches this component that has not been through it.
 */
import { useCallback, useEffect, useState, type DragEvent } from "react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { DRAG_MOVE, DRAG_NEW } from "../catalog";
import { columnsOf } from "../reducer";
import { innerWidth, renderBlock } from "../render";
import { useBuilder } from "../store";
import { COLUMN_CHILD_TYPES, type Block, type BlockType, type ColumnSlot, type PageStyle } from "../types";
import { BlockFrame } from "./BlockFrame";

/** Where a drop would land: which column (null = the letter itself) and the
 *  index inside it. Held in state so the gap can be drawn before the drop. */
interface DropAt {
  colId: string | null;
  index: number;
}

const COLUMN_ALLOWED = new Set<string>(COLUMN_CHILD_TYPES);

export function Canvas() {
  const t = useT();
  const { design, selectedId, dispatch, disabled } = useBuilder();
  const [dropAt, setDropAt] = useState<DropAt | null>(null);

  const page = design.page;
  const width = innerWidth(page);

  useDeleteKey(
    selectedId,
    disabled,
    useCallback((id: string) => dispatch({ kind: "remove", id }), [dispatch]),
  );

  /** A drag is ours when it carries one of our two keys. Anything else - a file,
   *  a selection from another tab - is left to the browser. */
  function dragPayload(e: DragEvent): { type: BlockType | null; id: string | null } | null {
    const types = e.dataTransfer.types;
    const isNew = types.includes(DRAG_NEW);
    const isMove = types.includes(DRAG_MOVE);
    if (!isNew && !isMove) return null;
    return {
      type: isNew ? (e.dataTransfer.getData(DRAG_NEW) as BlockType) || null : null,
      id: isMove ? e.dataTransfer.getData(DRAG_MOVE) || null : null,
    };
  }

  function handleDrop(e: DragEvent, colId: string | null, index: number) {
    const payload = dragPayload(e);
    setDropAt(null);
    if (!payload || disabled) return;
    e.preventDefault();
    e.stopPropagation();

    if (payload.id) {
      dispatch({ kind: "move", id: payload.id, target: colId ? { colId } : null, index });
      return;
    }
    if (!payload.type) return;
    if (colId) dispatch({ kind: "addToColumn", parentId: "", colId, type: payload.type, index });
    else dispatch({ kind: "add", type: payload.type, index });
  }

  /** A gap between two blocks, and the only drop target. Explicit gaps beat
   *  measuring the pointer against every block's midpoint: the target is where
   *  the block will actually go, so there is nothing to guess and nothing to
   *  get wrong near a block's edge. */
  function Gap({ colId, index }: { colId: string | null; index: number }) {
    const active = dropAt?.colId === colId && dropAt?.index === index;
    return (
      <div
        onDragOver={(e) => {
          const payload = dragPayload(e);
          if (!payload || disabled) return;
          // A column will not take every block type; refusing during dragover
          // is what makes the cursor say so before the operator lets go.
          if (colId && payload.type && !COLUMN_ALLOWED.has(payload.type)) return;
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = payload.id ? "move" : "copy";
          setDropAt({ colId, index });
        }}
        onDragLeave={() => setDropAt((d) => (d?.colId === colId && d?.index === index ? null : d))}
        onDrop={(e) => handleDrop(e, colId, index)}
        className={cn(
          "relative -my-1 h-2 rounded-full transition-all",
          active && "my-1 h-2 bg-primary/70 shadow-[0_0_0_3px] shadow-primary/15",
        )}
      />
    );
  }

  function renderLeaf(block: Block, w: number) {
    return (
      <BlockFrame block={block}>
        <div dangerouslySetInnerHTML={{ __html: renderBlock(block, page, w) || placeholder(block, t) }} />
      </BlockFrame>
    );
  }

  /** A `columns` block is drawn by this component rather than by the renderer,
   *  because its cells have to be drop targets. The geometry copies the
   *  renderer's: equal shares, half a gutter on each inner edge. */
  function renderColumns(block: Block, w: number) {
    const cols = columnsOf(block);
    const gap = Number(block.props.gap ?? 24);
    const childWidth = Math.max(80, Math.floor((w - gap * (cols.length - 1)) / cols.length));
    return (
      <BlockFrame block={block}>
        <div className="flex" style={{ gap: `${gap}px` }}>
          {cols.map((col: ColumnSlot) => (
            <div
              key={col.id}
              className="min-w-0 flex-1 rounded-md border border-dashed border-border/60 p-1"
              onDragOver={(e) => {
                // An empty column has no gaps of its own to aim at, so the
                // column itself takes the drop.
                if ((col.children ?? []).length > 0) return;
                const payload = dragPayload(e);
                if (!payload || disabled) return;
                if (payload.type && !COLUMN_ALLOWED.has(payload.type)) return;
                e.preventDefault();
                e.stopPropagation();
                setDropAt({ colId: col.id, index: 0 });
              }}
              onDrop={(e) => (col.children ?? []).length === 0 && handleDrop(e, col.id, 0)}
            >
              {(col.children ?? []).length === 0 ? (
                <p
                  className={cn(
                    "rounded px-2 py-6 text-center text-[11px] text-muted-foreground/70",
                    dropAt?.colId === col.id && "bg-primary/10 text-primary",
                  )}
                >
                  {t("marketing.builder.dropHere")}
                </p>
              ) : (
                <>
                  <Gap colId={col.id} index={0} />
                  {(col.children ?? []).map((child, i) => (
                    <div key={child.id}>
                      {renderLeaf(child, childWidth)}
                      <Gap colId={col.id} index={i + 1} />
                    </div>
                  ))}
                </>
              )}
            </div>
          ))}
        </div>
      </BlockFrame>
    );
  }

  return (
    <div
      className="min-h-full flex-1 overflow-y-auto p-6"
      style={{ backgroundColor: page.backgroundColor }}
      onClick={() => dispatch({ kind: "select", id: null })}
      onDragEnd={() => setDropAt(null)}
    >
      <div
        className="mx-auto shadow-sm"
        style={{
          maxWidth: `${page.contentWidth}px`,
          backgroundColor: page.cardColor,
          borderRadius: `${page.radius}px`,
          padding: `${page.paddingY}px ${page.paddingX}px`,
          color: page.textColor,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {design.blocks.length === 0 ? (
          <EmptyCanvas page={page} onDrop={(e) => handleDrop(e, null, 0)} onOver={dragPayload} />
        ) : (
          <>
            <Gap colId={null} index={0} />
            {design.blocks.map((block, i) => (
              <div key={block.id}>
                {block.type === "columns" ? renderColumns(block, width) : renderLeaf(block, width)}
                <Gap colId={null} index={i + 1} />
              </div>
            ))}
          </>
        )}
      </div>

    </div>
  );
}

/** A block that renders to nothing - an image with no source, an empty list -
 *  would otherwise become an invisible row nobody can select to fix. */
function placeholder(block: Block, t: (k: string) => string): string {
  return (
    `<div style="border:1px dashed rgba(120,120,120,.5);border-radius:6px;padding:14px;` +
    `text-align:center;font-size:12px;opacity:.7;">${t(`marketing.builder.blockEmpty.${block.type}`)}</div>`
  );
}

function EmptyCanvas({
  page,
  onDrop,
  onOver,
}: {
  page: PageStyle;
  onDrop: (e: DragEvent) => void;
  onOver: (e: DragEvent) => unknown;
}) {
  const t = useT();
  return (
    <div
      onDragOver={(e) => {
        if (!onOver(e)) return;
        e.preventDefault();
      }}
      onDrop={onDrop}
      className="rounded-lg border border-dashed px-6 py-16 text-center"
      style={{ borderColor: `${page.textColor}33` }}
    >
      <p className="text-sm font-medium">{t("marketing.builder.emptyTitle")}</p>
      <p className="mt-1.5 text-xs opacity-70">{t("marketing.builder.emptyHint")}</p>
    </div>
  );
}

/** Delete removes the selected block. Same guard as undo: inside a text field
 *  the browser's own Backspace is what the operator means. */
function useDeleteKey(selectedId: string | null, disabled: boolean, onDelete: (id: string) => void) {
  useEffect(() => {
    if (!selectedId || disabled) return;
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable) return;
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      e.preventDefault();
      onDelete(selectedId!);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, disabled, onDelete]);
}
